import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService, ConfirmationService, PrimeNGConfig } from 'primeng/api';
import { Component, OnInit, Input, EventEmitter, Output, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import { HotelListingService } from '../hotel-listing.service';
import { HotelListing } from '../hotel-listing';
import { PropertyService } from '@app/_components/property/property.service';

/**
 * Hotel Room V2 — add / edit / delete dialog for one str_hotel_listing row.
 *
 * Sibling of CompositeListingComponent. Differences:
 *   - parentListingType is always 3 here (Hotel Room) — the surrounding tab only shows when type=3.
 *   - allowed children are listingType=1 (standard STR) or listingType=2 (Composite); listingType=3
 *     (nested Hotel Room) is rejected inline.
 *   - Form fields: hotelShortTermRentalId (parent), shortTermRentalId (child), listingPriority.
 */
@Component({
    selector: 'app-hotel-listing',
    templateUrl: './hotel-listing.component.html',
    styleUrls: ['./hotel-listing.component.css'],
    providers: [MessageService, ConfirmationService],
    standalone: false
})
export class HotelListingComponent implements OnInit {
	properties: any;
	hotelShortTermRentalId: any;
	sitePriorities: { name: string; value: number; }[];
	/** Child STR ids already mapped to this hotel-room parent — used for duplicate-add prevention. */
	existingChildIds: number[] = [];

	constructor(
		private primengConfig: PrimeNGConfig,
		private hotelListingService: HotelListingService,
		private alertService: AlertService,
		private messageService: MessageService,
		private confirmationService: ConfirmationService,
		private propertyService: PropertyService,
		private formBuilder: UntypedFormBuilder,
		@Inject(MAT_DIALOG_DATA) data: any) {
		this.formTitle = data.title;
		this.hotelListing = data.hotelListing;
		this.hotelShortTermRentalId = data.hotelShortTermRentalId;
		this.existingChildIds = (data.existingChildIds || []).map((id: any) => Number(id));
		this.statuses = hotelListingService.statuses;
		this.sitePriorities = hotelListingService.sitePriorities;
	}

	@Input() hotelListing: HotelListing;
	@Output() doAdd = new EventEmitter<any>();
	@Output() doUpdate = new EventEmitter<any>();
	@Output() doDelete = new EventEmitter<any>();

	hotelListingId: number;

	form1: UntypedFormGroup;
	formLoading = false;
	formAlert: String;
	formTitle: String;

	statuses: any;

	validationMessages = {
		hotelListingId: [],
		hotelShortTermRentalId: [
			{ type: 'required', message: 'Required' }
		],
		shortTermRentalId: [
			{ type: 'required', message: 'Required' }
		],
		listingPriority: []
	};

	ngOnInit() {
		this.fetchProperties();
		this.primengConfig.ripple = true;
		this.formBuild();

		if (this.hotelListing) {
			this.hotelListingId = this.hotelListing.hotelListingId;
			this.form1.patchValue(this.hotelListing);
			// Lock parent in edit mode — hotelShortTermRentalId must not change after creation.
			this.form1.get('hotelShortTermRentalId').disable();
		}
	}

	fetchProperties() {
		if (!this.properties || this.properties === undefined || this.properties.length < 1) {
			try {
				this.propertyService.propertySummaryForShortData()
					.subscribe(
						res => {
							this.properties = res.data.properties;
							if (this.hotelShortTermRentalId) {
								this.form1.patchValue({
									hotelShortTermRentalId: Number(this.hotelShortTermRentalId)
								});
							}
							return this.properties;
						},
						err => {
							console.log('hotelListing-fetch-properties FAIL: ' + JSON.stringify(err));
							this.alertService.error(err);
						},
						() => { });
			} catch (error) {
				console.log('Form Invalid: ' + JSON.stringify(error));
				this.alertService.error(error);
			}
		}
		else {
			return (this.properties);
		}
	}

	onFormDelete() {
		this.confirmationService.confirm({
			message: 'Are you sure you want to delete Id# ' + this.hotelListingId + '?',
			header: 'Confirm',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				this.formDelete();
			},
			reject: () => {
				this.messageService.add({ severity: 'info', summary: 'Canceled', detail: 'Delete canceled.', life: 3000 });
			}
		});
	}

	/**
	 * Child dropdown options — Hotel Room parent allows standard STR (listingType=1)
	 * OR Composite (listingType=2). Nested Hotel Rooms (listingType=3) are excluded.
	 */
	get childPropertyOptions(): any[] {
		if (!this.properties) return [];
		return this.properties.filter((p: any) => p.listingType == null || p.listingType === 1 || p.listingType === 2);
	}

	/**
	 * Returns the propertyCode of the current hotel-room parent (for read-only display in edit mode).
	 * Uses getRawValue() so the disabled control value is included.
	 */
	getParentPropertyCode(): string {
		const parentId = this.form1?.getRawValue()?.hotelShortTermRentalId;
		if (!parentId || !this.properties) return '—';
		const p = this.properties.find((x: any) => x.shortTermRentalId == parentId);
		return p?.propertyCode ?? '—';
	}

	/**
	 * Duplicate-add guard — checks whether the selected child is already mapped to this parent.
	 * Only runs in add mode; in edit mode the parent is locked and this path is skipped.
	 */
	private validateNoDuplicate(): boolean {
		const childId = Number(this.form1.get('shortTermRentalId')?.value);
		if (!childId) return true;
		if (this.existingChildIds.includes(childId)) {
			this.messageService.add({
				severity: 'error',
				summary: 'Duplicate Child',
				detail: 'This unit is already a child of this Hotel Room parent.',
				life: 5000
			});
			return false;
		}
		return true;
	}

	/**
	 * Inline validation — reject Hotel Room (3) children.
	 * Mirrors HotelListingServiceIMPL.validateNestingRules on the server.
	 */
	private validateChildListingType(): boolean {
		const childId = this.form1.get('shortTermRentalId')?.value;
		if (!childId || !this.properties) return true;
		const child = this.properties.find((p: any) => p.shortTermRentalId == childId);
		if (!child) return true;
		if (child.listingType == null) return true;

		if (child.listingType === 3) {
			this.messageService.add({
				severity: 'error',
				summary: 'Invalid Child Listing',
				detail: 'Hotel Room parents may only contain standard STR or Composite children. Selected child is itself a Hotel Room — nested Hotel Rooms are not allowed.',
				life: 6000
			});
			return false;
		}
		return true;
	}

	formAdd() {
		try {
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			if (!this.validateChildListingType()) {
				return;
			}
			if (!this.validateNoDuplicate()) {
				return;
			}
			this.formLoading = true;
			this.hotelListingService.add(this.form1.value)
				.subscribe(
					(res: any) => {
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.hotelListing = res.data.hotelListing;
						this.doAdd.emit(this.hotelListing);
					},
					(err: any) => {
						console.log('hotelListing-add FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });

		} catch (error) {
			this.alertService.error(error);
		}
	}

	formUpdate() {
		try {
			this.form1.markAllAsTouched();
			if (this.form1.invalid) {
				return;
			}
			if (!this.validateChildListingType()) {
				return;
			}
			this.formLoading = true;

			// getRawValue() includes the disabled hotelShortTermRentalId field.
			this.hotelListingService.update(this.form1.getRawValue())
				.subscribe(
					(res: any) => {
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.hotelListing = res.data.hotelListing;
						this.doUpdate.emit(this.hotelListing);
					},
					(err: any) => {
						console.log('hotelListing-update FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			console.log('Form Invalid: ' + JSON.stringify(error));
			this.alertService.error(error);
		}
	}

	formDelete() {
		try {
			this.hotelListingService.delete(this.hotelListing.hotelListingId)
				.subscribe(
					(res: any) => {
						this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message });
						this.doDelete.emit(this.hotelListing);
					},
					(err: any) => {
						console.log('hotelListing-delete FAIL: ' + JSON.stringify(err));
						this.alertService.error(err);
					},
					() => { this.formLoading = false; });
		} catch (error) {
			this.alertService.error(error);
		}
	}

	formBuild() {
		this.form1 = this.formBuilder.group({
			hotelListingId: [this.hotelListingId],
			hotelShortTermRentalId: ['', Validators.required],
			shortTermRentalId: ['', Validators.required],
			listingPriority: []
		});
	}
}
